import { motion } from 'motion/react';

interface ChargingProgressBarProps {
  currentStep: number;
  totalSteps: number;
  labels?: string[];
}

export function ChargingProgressBar({ currentStep, totalSteps, labels }: ChargingProgressBarProps) {
  const fillPercent = (currentStep / totalSteps) * 100;

  return (
    <div className="w-full">
      {/* Labels row */}
      {labels && (
        <div className="flex justify-between mb-2 px-1">
          {labels.map((label, i) => (
            <span
              key={label}
              className={`text-xs font-medium transition-colors duration-300 ${
                i + 1 <= currentStep
                  ? 'text-[#8bc43e]'
                  : 'text-muted-foreground'
              }`}
            >
              {label}
            </span>
          ))}
        </div>
      )}

      {/* Battery body */}
      <div className="relative flex items-center">
        <div className="flex-1 h-3 rounded-full bg-muted/60 dark:bg-muted/40 border border-border/50 relative overflow-hidden">
          {/* Animated fill */}
          <motion.div
            className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-[#8bc43e] to-[#6fa02e]"
            animate={{ width: `${fillPercent}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            {/* Shimmer sweep */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
              animate={{ x: ['-100%', '200%'] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
            />
          </motion.div>

          {/* Step dots */}
          {Array.from({ length: totalSteps - 1 }, (_, i) => {
            const pos = ((i + 1) / totalSteps) * 100;
            return (
              <div
                key={i}
                className="absolute top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-background/60"
                style={{ left: `${pos}%`, transform: 'translate(-50%, -50%)' }}
              />
            );
          })}
        </div>

        {/* Battery terminal nub */}
        <div className="w-1.5 h-2.5 rounded-r-sm bg-muted-foreground/30 ml-0.5" />
      </div>

      {/* Step counter */}
      <p className="text-xs text-muted-foreground text-center mt-2">
        Step {currentStep} of {totalSteps}
      </p>
    </div>
  );
}
